import type {
  OpportunityCategory,
  Device,
  Location,
  OrganizationType,
  OrganizationStatus,
  ApiValue
} from './properties';

export enum EventName {
  UserSignedUp = 'User Signed Up',
  UserLoggedIn = 'User Logged In',
  OrganizationInterestApplicationSubmitted = 'Organization Interest Application Submitted',
  OpportunityCreated = 'Opportunity Created',
  OpportunityPublished = 'Opportunity Published',
  OpportunitiesSearched = 'Opportunities Searched',
  OpportunityViewed = 'Opportunity Viewed',
  OpportunityShared = 'Opportunity Shared',
  RegistrationStarted = 'Registration Started',
  RegistrationSubmitted = 'Registration Submitted',
  CheckInCompleted = 'Check-In Completed',
  PartnerSignUpComplete = 'Partner Sign-Up Complete',
  KindnessChallengeEntrySubmitted = 'Kindness Challenge Entry Submitted',
  OrganizationApproved = 'Organization Approved',
  OrganizationRejected = 'Organization Rejected',
  OrganizationInReview = 'Organization In Review'
}

export interface BaseEventProperties {
  /**
   * RudderStack requires at least one of userId or anonymousId on every event.
   * When omitted, the analytics client falls back to the identity set via
   * identifyUser() / setUserContext(), or the browser SDK's auto-managed anonymousId.
   */
  userId?: string;
  anonymousId?: string;
  timestamp?: string;
  sessionId?: string;
  [key: string]: ApiValue;
}

// Account + Auth Events
export interface UserSignedUpProperties extends BaseEventProperties {
  device?: Device;
}

export interface UserLoggedInProperties extends BaseEventProperties {
  device?: Device;
}

export interface OrganizationInterestApplicationSubmittedProperties
  extends BaseEventProperties {
  organizationName: string | null;
  organizationType: OrganizationType | null;
  accountStatus: OrganizationStatus;
}

export interface PartnerSignUpCompleteProperties extends BaseEventProperties {
  organizationName: string | null;
  organizationType: OrganizationType | null;
}

// Opportunity Management

export interface OpportunityCreatedProperties extends BaseEventProperties {
  opportunityId: string;
  opportunityCategory: OpportunityCategory;
  volunteersNeeded: number;
  location: Location;
  opportunityDate: string; // ISO 8601
}

export interface OpportunityPublishedProperties extends BaseEventProperties {
  opportunityId: string;
}

// Opportunity Discovery (Volunteer)

export interface OpportunitiesSearchedProperties extends BaseEventProperties {
  searchQuery?: string;
  filters: {
    category?: OpportunityCategory[];
    location?: string;
  };
}

export interface OpportunityViewedProperties extends BaseEventProperties {
  opportunityId: string;
  opportunityTitle: string;
  organizationId: string;
  organizationName: string;
  opportunityCategory: OpportunityCategory;
}

export interface OpportunitySharedProperties extends BaseEventProperties {
  opportunityId: string;
}

// Application + Registration

export interface RegistrationStartedProperties extends BaseEventProperties {
  opportunityId: string;
}

export interface RegistrationSubmittedProperties extends BaseEventProperties {
  opportunityId: string;
  registrationId: string;
  answeredQuestions?: number;
  timeToCompleteInSeconds?: number;
}

export interface CheckInCompletedProperties extends BaseEventProperties {
  opportunityId: string;
  registrationId: string;
  checkInMethod: string; //this can be restricted to specific values as the feature is implemented
  isOnTime: boolean;
  minutesEarlyOrLate: number; // negative values equate to early check in
}

// Kindness Challenge

export interface KindnessChallengeEntrySubmittedProperties
  extends BaseEventProperties {
  email: string;
  firstName: string;
  lastName: string;
  kindnessChallengeIdentifier: string;
}

// Organization Events

interface OrganizationStatusChangeProperties extends BaseEventProperties {
  organizationId: string;
  organizationName: string;
  organizationType: string | null;
  reviewerId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OrganizationApprovedProperties
  extends OrganizationStatusChangeProperties {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OrganizationRejectedProperties
  extends OrganizationStatusChangeProperties {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OrganizationInReviewProperties
  extends OrganizationStatusChangeProperties {}

// Event Name to Properties Map

export interface EventPropertiesMap {
  [EventName.UserSignedUp]: UserSignedUpProperties;
  [EventName.UserLoggedIn]: UserLoggedInProperties;
  [EventName.OrganizationInterestApplicationSubmitted]: OrganizationInterestApplicationSubmittedProperties;
  [EventName.OpportunityCreated]: OpportunityCreatedProperties;
  [EventName.OpportunityPublished]: OpportunityPublishedProperties;
  [EventName.OpportunitiesSearched]: OpportunitiesSearchedProperties;
  [EventName.OpportunityViewed]: OpportunityViewedProperties;
  [EventName.OpportunityShared]: OpportunitySharedProperties;
  [EventName.RegistrationStarted]: RegistrationStartedProperties;
  [EventName.RegistrationSubmitted]: RegistrationSubmittedProperties;
  [EventName.CheckInCompleted]: CheckInCompletedProperties;
  [EventName.PartnerSignUpComplete]: PartnerSignUpCompleteProperties;
  [EventName.KindnessChallengeEntrySubmitted]: KindnessChallengeEntrySubmittedProperties;
  [EventName.OrganizationApproved]: OrganizationApprovedProperties;
  [EventName.OrganizationRejected]: OrganizationRejectedProperties;
  [EventName.OrganizationInReview]: OrganizationInReviewProperties;
}
